import { useEffect, useRef, useState, useCallback } from "react";
import { useSocketIo } from "./useSocketIo";

/**
 * useAutoRefresh — adds the same WebSocket + visibility + interval refresh
 * pattern that OrdersPage uses to any list page.
 *
 * @param {Function} refetch  Async fetcher; will be called as refetch({ silent: true }).
 * @param {Object}   options
 *  - user            : { user_id, role } from useAuth (required for WS)
 *  - eventTypes      : array of substrings; refetch fires when notification_type contains any (case-insensitive)
 *  - pollIntervalMs  : default 30000 (30 seconds)
 *  - enabled         : default true
 *
 * Returns:
 *  - lastUpdatedAt: Date | null
 *  - refreshing: boolean
 *  - manualRefresh: () => Promise<void>  (use to wire a Refresh button)
 */
export function useAutoRefresh(refetch, {
  user,
  eventTypes = [],
  pollIntervalMs = 30000,
  enabled = true,
} = {}) {
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const tick = useCallback(async (opts = { silent: true }) => {
    if (!refetchRef.current) return;
    try {
      await refetchRef.current(opts);
      setLastUpdatedAt(new Date());
    } catch {
      // refetcher should handle its own errors / toast
    }
  }, []);

  // Tab visibility + focus
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") tick({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled, tick]);

  // Silent polling
  useEffect(() => {
    if (!enabled || !pollIntervalMs) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") tick({ silent: true });
    }, pollIntervalMs);
    return () => clearInterval(id);
  }, [enabled, pollIntervalMs, tick]);

  // Socket.IO subscription (replaces legacy /api/ws/notifications) — P1, iter 73.
  // Backend `core/realtime.broadcast_event` fans every legacy WS event to the
  // new Socket.IO server, so listening on the `realtime_update` channel here
  // gets us the exact same payloads (type/data) the old code consumed.
  const types = (eventTypes || []).map((t) => String(t).toLowerCase());
  useSocketIo({
    realtime_update: (msg) => {
      const t = String(msg?.event || msg?.type || "").toLowerCase();
      if (types.length === 0 || types.some((kw) => t.includes(kw))) {
        tick({ silent: true });
      }
    },
    notification: (msg) => {
      const t = String(msg?.notification_type || msg?.type || "").toLowerCase();
      if (types.length === 0 || types.some((kw) => t.includes(kw))) {
        tick({ silent: true });
      }
    },
  }, { enabled: enabled && !!user?.user_id });

  const manualRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await tick({ silent: false });
    } finally {
      setRefreshing(false);
    }
  }, [tick]);

  return { lastUpdatedAt, refreshing, manualRefresh };
}

export default useAutoRefresh;
